import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import * as moment from 'moment';
import produce from 'immer';
import { orderBy } from 'lodash-es';
import { mapPharmOrder, PharmOrder } from '../../models';
import { ApiService, EnvService, SentryService } from '../../services';
import { defaultApiStoreIterableData, ApiStoreData, CcsSentryError, MissingApiRequestDataError } from '../utils';
import { FetchPharmOrders, SetSelectedEnterpriseOrderId } from './pharm-orders.action';
import { PatientState } from '../patient/patient.state';

export interface PharmOrdersStateModel {
  selectedEnterpriseOrderId: string | null;
  pharmOrders: ApiStoreData<PharmOrder[]>;
}
export const pharmOrdersDefaults = {
  selectedEnterpriseOrderId: null,
  pharmOrders: defaultApiStoreIterableData,
};

@State<PharmOrdersStateModel>({
  name: 'pharmorders',
  defaults: pharmOrdersDefaults,
})
@Injectable({ providedIn: 'root' })
export class PharmOrdersState {
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static selectedEnterpriseOrderId(state: PharmOrdersStateModel): string | null {
    return state.selectedEnterpriseOrderId;
  }

  @Selector()
  static activePharmOrders(state: PharmOrdersStateModel): PharmOrder[] {
    // Active Pharm orders
    return state.pharmOrders.data;
  }

  @Selector()
  static isLoading(state: PharmOrdersStateModel): boolean {
    return state.pharmOrders.isLoading;
  }

  @Selector()
  static error(state: PharmOrdersStateModel): CcsSentryError | null {
    return state.pharmOrders.error;
  }

  @Action(FetchPharmOrders)
  fetchPharmOrders(ctx: StateContext<PharmOrdersStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.pharmOrders.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: null });
    }

    const processOrderData = (data: PharmOrder[]) => {
      /*const hasOrdersToAuthorize = data;
      // There is a bug in the library's gtag set() function. Using gtag on window straight.
      // TODO Refine this later by replacing the gtag library we are using
      (window as any).gtag('set', this.envService.googleAnalytics.measurementId, {
        has_orders_to_authorize: hasOrdersToAuthorize,
      });*/

      this.handleSuccess({ ctx, data, stateKey: 'pharmOrders' });
    };

    return this.apiService.getPharmOrders(patientId).pipe(
      tap({
        next: (response) => {
          const data = response.data.map((p) => mapPharmOrder(p));
          processOrderData(data);

          ctx.setState(
            produce((draft) => {
              draft.pharmOrders.data = data;
            })
          );
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'pharmOrders' }))
    );
  }
}
